/**
 * @fileoverview Country-aware Zip/Postal code helpers for billing forms.
 *
 * Only US, AU and CA have explicit format rules today; every other country is
 * treated as "required, but not format-validated". Country codes are ISO
 * 3166-1 alpha-2 (e.g. 'US', 'CA', 'AU') as produced by {@link CountrySelect}.
 */

type SupportedCountry = 'US' | 'CA' | 'AU';

interface PostalRule {
  /** Validation pattern applied to the normalized value. */
  pattern: RegExp;
  /** Error message shown when the normalized value fails {@link pattern}. */
  message: string;
  /** Max characters accepted by the input for this country. */
  maxLength: number;
  /** Field label appropriate for the country's convention. */
  label: string;
  /** Placeholder illustrating the expected format. */
  placeholder: string;
  /** Hint for the on-screen keyboard on mobile devices. */
  inputMode: 'numeric' | 'text';
}

const POSTAL_RULES: Record<SupportedCountry, PostalRule> = {
  US: {
    pattern: /^\d{5}$/,
    message: 'Enter a valid 5-digit US zip code.',
    maxLength: 5,
    label: 'Zip Code',
    placeholder: '94105',
    inputMode: 'numeric',
  },
  AU: {
    pattern: /^\d{4}$/,
    message: 'Enter a valid 4-digit Australian postcode.',
    maxLength: 4,
    label: 'Postcode',
    placeholder: '2000',
    inputMode: 'numeric',
  },
  CA: {
    // Letter-Number-Letter-Number-Letter-Number, e.g. J3Y4L2.
    pattern: /^[A-Z]\d[A-Z]\d[A-Z]\d$/,
    message: 'Enter a valid Canadian postal code (e.g. J3Y4L2).',
    maxLength: 6,
    label: 'Postal Code',
    placeholder: 'J3Y4L2',
    inputMode: 'text',
  },
};

const DEFAULT_FIELD_CONFIG = {
  label: 'Zip/Postal',
  placeholder: '',
  inputMode: 'text' as const,
  maxLength: undefined as number | undefined,
};

const isSupportedCountry = (country: string): country is SupportedCountry =>
  country in POSTAL_RULES;

/**
 * Normalizes a raw postal value to the canonical form used for validation and
 * storage: US/AU keep digits only; CA is uppercased with spaces/dashes removed;
 * all other countries are simply trimmed.
 */
export function normalizePostalCode(country: string, value: string): string {
  const trimmed = value.trim();

  if (country === 'US' || country === 'AU') {
    return trimmed.replace(/\D/g, '');
  }
  if (country === 'CA') {
    return trimmed.replace(/[\s-]/g, '').toUpperCase();
  }
  return trimmed;
}

/** Result of validating a postal code against a country's rules. */
export type PostalValidationResult = { valid: true } | { valid: false; message: string };

/**
 * Validates a postal code for the given country. Empty values are always
 * invalid (the field is required). Countries without an explicit rule pass as
 * long as a non-empty value is supplied.
 */
export function validatePostalCode(country: string, rawValue: string): PostalValidationResult {
  const value = normalizePostalCode(country, rawValue);

  if (!value) {
    return { valid: false, message: 'Zip/Postal is required.' };
  }

  if (!isSupportedCountry(country)) {
    return { valid: true };
  }

  const rule = POSTAL_RULES[country];
  return rule.pattern.test(value) ? { valid: true } : { valid: false, message: rule.message };
}

/** Input-facing configuration (label, placeholder, keyboard, max length) per country. */
export function getPostalFieldConfig(country: string): {
  label: string;
  placeholder: string;
  inputMode: 'numeric' | 'text';
  maxLength?: number;
} {
  if (!isSupportedCountry(country)) {
    return { ...DEFAULT_FIELD_CONFIG };
  }
  const { label, placeholder, inputMode, maxLength } = POSTAL_RULES[country];
  return { label, placeholder, inputMode, maxLength };
}
